import React, { useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Grid, IconButton, Link, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { deleteAffiliatePage } from "API/calls";
import BackdropLoading from "components/elements/BackdropLoading";
import Badge from "components/elements/Badge";
import { BadgeColors } from "components/elements/Badge/types";
import { Edit, Trash } from "components/icons";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useAPIAffiliatePages } from "hooks/useAPI";
import useConfirm from "hooks/useConfirm";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCopyToClipboard } from "react-use";
import { AffiliatePageState } from "types/enums";

const StyledBox = styled(Box)(() => {
  const { colors } = useMUIThemeModeContext();

  return {
    width: "100%",
    height: "100%",
    backgroundColor: alpha(colors["gray450"], 0.05),
    borderRadius: "16px",
  };
});

const ItemBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "odd" && prop !== "isFirst" && prop !== "isLast",
})<ItemBoxProps>(({ theme, odd, isFirst, isLast }) => {
  const { colors } = useMUIThemeModeContext();

  return {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
    backgroundColor: odd ? colors["white"] : "transparent",
    borderTopLeftRadius: isFirst ? "8px" : 0,
    borderTopRightRadius: isFirst ? "8px" : 0,
    borderBottomLeftRadius: isLast ? "8px" : 0,
    borderBottomRightRadius: isLast ? "8px" : 0,
  };
});

interface ItemBoxProps {
  odd?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
}

const OwnPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useMUIThemeModeContext();
  const { confirm } = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const [, copyToClipboard] = useCopyToClipboard();

  const [isOpenBackdropLoading, setIsOpenBackdropLoading] = useState<boolean>(false);
  const { data, mutate } = useAPIAffiliatePages();

  if (!data) {
    return (
      <StyledBox p={8}>
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={32} width={"75%"} sx={{ borderRadius: "8px", mb: 2 }} />
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" height={52} sx={{ borderRadius: "8px" }} />
        </Stack>
      </StyledBox>
    );
  }

  if (data.affiliatePages.length === 0) {
    return (
      <StyledBox
        p={{ xs: 4, md: 8 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        textAlign={"center"}
        flexDirection="column"
        rowGap={6}>
        <Typography fontSize={"1.5rem"} fontWeight={fontWeight.semiBold} lineHeight={"32px"} color={"tertiary.main"}>
          {t("affiliate.ownPage.empty.title")}
        </Typography>
        <Typography fontSize={"0.875rem"} fontWeight={fontWeight.regular} lineHeight={"22px"}>
          {<Trans i18nKey="affiliate.ownPage.empty.description" />}
        </Typography>
        <Stack spacing={3}>
          <Button variant="contained" color="primary" onClick={() => navigate("/nova-stranka-doporuceni")}>
            {t("affiliate.ownPage.empty.cta")}
          </Button>
          <Button variant="text" color="primary" href={"/doporucuje#lubos-ukazka"} endIcon={<OpenInNewIcon />}>
            {t("affiliate.ownPage.empty.example")}
          </Button>
        </Stack>
      </StyledBox>
    );
  }

  const BadgeColor = (state: AffiliatePageState): BadgeColors => {
    switch (state) {
      case AffiliatePageState.ACTIVE:
        return "success";
      case AffiliatePageState.PENDING:
        return "warning";
      case AffiliatePageState.DRAFT:
        return "blue";
      default:
        return "info"; // Default case for any other state
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await confirm({
        title: t("affiliate.ownPage.list.confirmDelete.title"),
        description: t("affiliate.ownPage.list.confirmDelete.description"),
        dialogProps: { maxWidth: "xs" },
      });

      setIsOpenBackdropLoading(true);
      try {
        await deleteAffiliatePage(id);
        enqueueSnackbar(t("affiliate.ownPage.list.deleteSuccess"), { variant: "success" });
        mutate();
      } catch (error: any) {
        enqueueSnackbar(t("affiliate.ownPage.list.deleteError"), { variant: "error" });
      }
      setIsOpenBackdropLoading(false);
    } catch (err: any) {
      // silent
    }
  };

  const onCopyButtonClick = (path: string) => () => {
    const fullPath = `${window.location.origin}/doporucuje#${path}`;
    copyToClipboard(fullPath);
    enqueueSnackbar(t("affiliate.copiedToClipboard"), { variant: "success" });
  };

  const publicPath = (path: string, state: AffiliatePageState, id: string) => {
    const redirectUrl = state === AffiliatePageState.ACTIVE ? `/doporucuje#${path}` : `/doporucuje-nahled#${id}`;

    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Link href={redirectUrl} style={{ textDecoration: "none", color: "inherit" }}>
          <Typography fontSize={"0.75rem"} color={"text.secondary"}>
            {t(`affiliate.ownPage.list.link`) + path}
          </Typography>
        </Link>
        <Tooltip arrow placement="top" title={t("copyToClipboard") ?? ""}>
          <IconButton size="small" color="info" onClick={onCopyButtonClick(path)}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <StyledBox p={{ xs: 1.5, sm: 2, md: 4 }} rowGap={3} display="flex" flexDirection="column">
      <Typography fontSize={"1.5rem"} fontWeight={fontWeight.semiBold} lineHeight={"32px"} color={"tertiary.main"}>
        {t("affiliate.ownPage.list.title")}
      </Typography>
      <Box>
        {data.affiliatePages.map((page, index) => (
          <ItemBox
            key={page.id}
            odd={index % 2 === 0}
            isFirst={index === 0}
            isLast={index === data.affiliatePages.length - 1}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8} sm={6}>
                <Typography fontSize={"1rem"} fontWeight={fontWeight.semiBold}>
                  {page.nickname}
                </Typography>
                <Box display={{ xs: "flex", sm: "none" }} alignItems="center" mt={0.5}>
                  <Badge color={BadgeColor(page.state)} withTextTransform={false}>
                    <Typography fontSize={"0.75rem"} fontWeight={fontWeight.medium}>
                      {t(`affiliate.ownPage.list.state.${page.state}`)}
                    </Typography>
                  </Badge>
                </Box>
              </Grid>
              <Grid item xs={3} sx={{ display: { xs: "none", sm: "block" } }}>
                <Badge color={BadgeColor(page.state)} withTextTransform={false}>
                  <Typography fontSize={"0.75rem"} fontWeight={fontWeight.medium}>
                    {t(`affiliate.ownPage.list.state.${page.state}`)}
                  </Typography>
                </Badge>
              </Grid>
              <Grid item xs={4} sm={3}>
                <Stack direction={"row"} display="flex" justifyContent="flex-end" spacing={0.5}>
                  {page.state !== AffiliatePageState.PENDING && (
                    <IconButton size="small" onClick={() => navigate(`/upravit-stranku-doporuceni/${page.id}`)}>
                      <Edit sx={{ fontSize: 20, color: colors.gray700 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => handleDelete(page.id)}>
                    <Trash sx={{ fontSize: 20, color: colors.gray700 }} />
                  </IconButton>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                {publicPath(page.path, page.state, page.id)}
              </Grid>
            </Grid>
          </ItemBox>
        ))}
      </Box>
      <Box flexGrow={1} alignItems={"flex-end"} justifyContent={"center"} display="flex">
        <Button variant="contained" color="primary" fullWidth onClick={() => navigate("/nova-stranka-doporuceni")}>
          {t("affiliate.ownPage.empty.cta")}
        </Button>
      </Box>
      <BackdropLoading open={isOpenBackdropLoading} />
    </StyledBox>
  );
};

export default OwnPage;
